/**
 * TamDeclarationsPage — TAM nyilatkozatok kezelese
 * Epic 71: Futáros Kézbesítés & TAM
 *
 * TAM cikk: 0% AFA-val ertekesitheto termek — nyilatkozat koteles.
 */

import type { TamDeclarationMethod } from '@/api/tam';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useCreateTamDeclaration, useTamDeclarations } from '@/hooks/useTam';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';

export function TamDeclarationsPage() {
  const { t } = useTranslation();
  const { declarations, isLoading, error, refetch } = useTamDeclarations();
  const createMutation = useCreateTamDeclaration();

  const [form, setForm] = useState({
    invoiceId: '',
    method: 'APP' as TamDeclarationMethod,
  });

  const handleCreate = async () => {
    if (!form.invoiceId) return;
    await createMutation.mutateAsync({
      invoiceId: form.invoiceId,
      method: form.method,
    });
    setForm({ invoiceId: '', method: 'APP' });
    refetch();
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">{t('finance.tamDeclarationsTitle')}</h1>
        <div className="text-center py-8 text-muted-foreground">{t('finance.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">{t('finance.tamDeclarationsTitle')}</h1>
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-red-500">{t('finance.errorPrefix', { message: error })}</p>
            <Button className="mt-4" onClick={() => refetch()}>
              {t('finance.retry')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const METHOD_LABELS: Record<string, string> = {
    APP: t('finance.methodApp'),
    EMAIL: t('finance.methodEmail'),
    PAPER: t('finance.methodPaper'),
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">{t('finance.tamDeclarationsTitle')}</h1>

      {/* Create declaration */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t('finance.newTamDeclaration')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">{t('finance.tamDescription')}</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input
              placeholder={t('finance.invoiceIdPlaceholder')}
              value={form.invoiceId}
              onChange={e => setForm({ ...form, invoiceId: e.target.value })}
            />
            <select
              className="border rounded px-3 py-2 bg-background"
              value={form.method}
              onChange={e => setForm({ ...form, method: e.target.value as TamDeclarationMethod })}
            >
              <option value="APP">{t('finance.methodApp')}</option>
              <option value="EMAIL">{t('finance.methodEmail')}</option>
              <option value="PAPER">{t('finance.methodPaper')}</option>
            </select>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending
                ? t('finance.recordingDeclaration')
                : t('finance.recordDeclaration')}
            </Button>
          </div>
          {createMutation.isError && (
            <p className="text-red-500 text-sm mt-2">
              {t('finance.errorPrefix', {
                message: createMutation.error?.message ?? t('finance.unknownError'),
              })}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Declaration list */}
      {declarations.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            {t('finance.noTamDeclarations')}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>
              {t('finance.recordedDeclarations', { count: declarations.length })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {declarations.map(decl => (
                <div key={decl.id} className="py-3 flex justify-between items-center">
                  <div>
                    <span className="font-medium">
                      {t('finance.invoiceLabel', { id: decl.invoiceId })}
                    </span>
                    <span className="ml-4 text-sm text-muted-foreground">
                      {t('finance.methodLabel', {
                        method: METHOD_LABELS[decl.method] ?? decl.method,
                      })}
                    </span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {new Date(decl.declaredAt).toLocaleDateString('hu-HU')}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
